"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Mail, ChevronDown } from "lucide-react";

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full py-3.5 text-left"
      >
        <span className="text-sm font-medium pr-4">{question}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div
        className="grid transition-all duration-200"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <p className="text-sm text-muted-foreground pb-3.5">{answer}</p>
        </div>
      </div>
    </div>
  );
}

export function HelpClient({ user }: { user: any }) {
  const [copySuccess, setCopySuccess] = useState(false);
  const userEmail = user?.unique_email_address;

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-3xl">
      <div className="mb-8">
        <h1 className="font-display text-3xl tracking-tight mb-2">Help & FAQ</h1>
        <p className="text-muted-foreground">
          Everything you need to know about ForwardCents
        </p>
      </div>

      {/* Quick Start */}
      <div className="border rounded-xl p-5 mb-5">
        <h2 className="text-base font-medium mb-4">Quick Start</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { step: "1", title: "Set up forwarding", desc: "Configure your email to forward receipts" },
            { step: "2", title: "AI processes it", desc: "AI extracts expense details from PDFs" },
            { step: "3", title: "Track expenses", desc: "View and manage in your dashboard" },
          ].map((item) => (
            <div key={item.step} className="p-3.5 bg-muted/30 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-foreground text-background text-xs font-medium flex items-center justify-center mb-2.5">
                {item.step}
              </div>
              <span className="text-sm font-medium">{item.title}</span>
              <p className="text-sm text-muted-foreground mt-0.5">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Email Setup */}
      <div className="border rounded-xl p-5 mb-5">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-medium">Email Forwarding Setup</h2>
        </div>

        {userEmail ? (
          <div className="space-y-5">
            {/* Forwarding address */}
            <div className="flex items-center gap-2 p-2.5 bg-muted/30 border rounded-lg">
              <code className="flex-1 text-sm font-mono">{userEmail}</code>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => copyToClipboard(userEmail)}
              >
                {copySuccess ? "Copied" : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>

            {/* Gmail instructions */}
            <div>
              <h3 className="text-base font-medium mb-3">Gmail Setup</h3>
              <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                <li>Go to Gmail Settings &rarr; Forwarding and POP/IMAP</li>
                <li>Click &ldquo;Add a forwarding address&rdquo;</li>
                <li>Enter: <code className="bg-muted px-1.5 py-0.5 rounded text-sm text-foreground">{userEmail}</code></li>
                <li>Follow the verification steps</li>
                <li>Go to &ldquo;Filters and Blocked Addresses&rdquo;</li>
                <li>Create a new filter with: <code className="bg-muted px-1.5 py-0.5 rounded text-sm text-foreground">receipt OR invoice OR bill</code></li>
                <li>Set action: &ldquo;Forward it to&rdquo; your unique email</li>
                <li>Optionally check &ldquo;Keep Google's copy in inbox&rdquo;</li>
              </ol>
              <div className="mt-3 p-2.5 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Tip:</span> If you run into issues, reach out to{" "}
                  <a href="mailto:support@email.forwardcents.app" className="underline">support</a>.
                </p>
              </div>
            </div>

            {/* Tips */}
            <div className="p-4 border border-amber-200 dark:border-amber-800/50 bg-amber-50/50 dark:bg-amber-950/10 rounded-lg">
              <span className="text-base font-medium">Tips for Better Results</span>
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                <li>Include keywords like &ldquo;receipt&rdquo;, &ldquo;invoice&rdquo;, &ldquo;bill&rdquo; in filters</li>
                <li>Add specific vendor emails to forwarding rules</li>
                <li>You can always manually forward emails too</li>
                <li>PDF receipts work best — images may not process well</li>
                <li>Test with a few emails before setting up auto-forwarding</li>
              </ul>
            </div>

            {/* Copy templates */}
            <div className="space-y-2.5">
              <span className="text-base font-medium">Quick Copy</span>
              <div className="flex items-center gap-2 p-2.5 bg-muted/30 border rounded-lg">
                <div className="min-w-0 flex-1">
                  <label className="text-xs text-muted-foreground">Filter keywords</label>
                  <code className="text-sm block mt-0.5 truncate">receipt OR invoice OR bill OR expense OR payment</code>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 shrink-0"
                  onClick={() => copyToClipboard("receipt OR invoice OR bill OR expense OR payment")}
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <div className="flex items-center gap-2 p-2.5 bg-muted/30 border rounded-lg">
                <div className="min-w-0 flex-1">
                  <label className="text-xs text-muted-foreground">Forwarding email</label>
                  <code className="text-sm block mt-0.5 truncate">{userEmail}</code>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 shrink-0"
                  onClick={() => copyToClipboard(userEmail || "")}
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 border border-dashed rounded-lg">
            <p className="text-sm text-muted-foreground mb-3">
              Complete onboarding to get your unique forwarding email.
            </p>

            <Button asChild size="sm" className="rounded-full h-8 text-xs">
              <a href="/onboarding">Get Your Email Address</a>
            </Button>
          </div>
        )}
      </div>

      {/* FAQ */}
      <div className="border rounded-xl p-5">
        <h2 className="text-base font-medium mb-2">Frequently Asked Questions</h2>
        <div>
          <FAQItem
            question="How does ForwardCents work?"
            answer="ForwardCents processes receipt emails by extracting PDF attachments and using AI to automatically extract expense information like amount, date, vendor, and category. The processed expenses appear in your dashboard for review and management."
          />
          <FAQItem
            question="What email formats are supported?"
            answer="The system works best with PDF receipts attached to emails. It can process receipts from most vendors including restaurants, hotels, ride-sharing services, online purchases, and business expenses."
          />
          <FAQItem
            question="Is my data secure?"
            answer="Yes, all your expense data is encrypted and stored securely. Each user's data is completely isolated, and PDF documents are stored with user-specific access controls. Your forwarding email address is unique and only processes emails you send to it."
          />
          <FAQItem
            question="Can I edit expenses after they're processed?"
            answer="Absolutely! You can edit any expense details including amount, date, vendor, category, and reimbursement status directly from your expenses dashboard. You can also add notes and change the expense status."
          />
          <FAQItem
            question="What if the AI extracts incorrect information?"
            answer="The AI is quite accurate, but you can always edit any extracted information. Simply click on any expense in your dashboard to modify the details."
          />
          <FAQItem
            question="Can I forward emails manually?"
            answer="Yes! You don't need to set up automatic forwarding. You can manually forward receipt emails to your unique address whenever you receive them. This gives you complete control over which receipts get processed."
          />
        </div>
      </div>

      {/* FAQ Structured Data for Rich Results — hardcoded content, no user input */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "How does ForwardCents work?", "acceptedAnswer": { "@type": "Answer", "text": "ForwardCents processes receipt emails by extracting PDF attachments and using AI to automatically extract expense information like amount, date, vendor, and category. The processed expenses appear in your dashboard for review and management." } },
          { "@type": "Question", "name": "What email formats are supported?", "acceptedAnswer": { "@type": "Answer", "text": "The system works best with PDF receipts attached to emails. It can process receipts from most vendors including restaurants, hotels, ride-sharing services, online purchases, and business expenses." } },
          { "@type": "Question", "name": "Is my data secure?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, all your expense data is encrypted and stored securely. Each user's data is completely isolated, and PDF documents are stored with user-specific access controls. Your forwarding email address is unique and only processes emails you send to it." } },
          { "@type": "Question", "name": "Can I edit expenses after they're processed?", "acceptedAnswer": { "@type": "Answer", "text": "Absolutely! You can edit any expense details including amount, date, vendor, category, and reimbursement status directly from your expenses dashboard." } },
          { "@type": "Question", "name": "What if the AI extracts incorrect information?", "acceptedAnswer": { "@type": "Answer", "text": "The AI is quite accurate, but you can always edit any extracted information. Simply click on any expense in your dashboard to modify the details." } },
          { "@type": "Question", "name": "Can I forward emails manually?", "acceptedAnswer": { "@type": "Answer", "text": "Yes! You don't need to set up automatic forwarding. You can manually forward receipt emails to your unique address whenever you receive them. This gives you complete control over which receipts get processed." } },
        ]
      }) }} />
    </div>
  );
}
